import { FC, useCallback } from "react";
import {
    Card3Icon,
    DirectoryIcon,
    IntegrationsIcon,
    MembersIcon,
    PlansIcon,
    LockIcon,
} from "@icons";
import { useSettings } from "@components/settings/SettingsProvider";
import { useEntities } from "@hooks/useEntities";
import { EntityPaneKey, getEntityPaneKey } from "@components/settings/lib";
import { Command } from "@vendor/cmdk/cmdk";
import { EntityIcon } from "@components/entity/EntityIcon";
import {
    EntitySettingsPages,
    UserSettingsPages,
    WorkspaceSettingsPages,
} from "../settings/constants";
import { SettingsCommandItem } from "./SettingsCommandItem";
import { useCommands } from "./useCommands";

export const WorkspaceSettingsCommandsList: FC = () => {
    const { open: openSettings } = useSettings();
    const { close: closeCommands } = useCommands();

    const handleSelected = useCallback(
        (
            path: Array<
                WorkspaceSettingsPages | UserSettingsPages | EntityPaneKey
            >,
        ) => {
            openSettings(path);
            closeCommands();
        },
        [openSettings, closeCommands],
    );

    const entities = useEntities({
        requireAdminAccess: true,
    });

    return (
        <Command.Group heading="Workspace settings">
            <SettingsCommandItem
                path={WorkspaceSettingsPages.MEMBERS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.MEMBERS])
                }
                keywords={["custom"]}
            >
                <MembersIcon /> Go to Members
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.INTEGRATIONS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.INTEGRATIONS])
                }
            >
                <IntegrationsIcon /> Go to Integrations
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.DIRECTORY}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.DIRECTORY])
                }
            >
                <DirectoryIcon /> Go to Directory
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.LOCKS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.LOCKS])
                }
            >
                <LockIcon /> Go to Locks
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.PLANS}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.PLANS])
                }
            >
                <PlansIcon /> Go to Plans
            </SettingsCommandItem>
            <SettingsCommandItem
                path={WorkspaceSettingsPages.BILLING}
                onSelected={() =>
                    handleSelected([WorkspaceSettingsPages.BILLING])
                }
            >
                <Card3Icon /> Go to Billing
            </SettingsCommandItem>
            {entities.map((entity, index) => {
                const key = getEntityPaneKey(
                    EntitySettingsPages.SETTINGS,
                    entity,
                );
                return (
                    <SettingsCommandItem
                        key={entity.id}
                        validPathOverride={key}
                        onSelected={() =>
                            handleSelected([
                                getEntityPaneKey(
                                    EntitySettingsPages.SETTINGS,
                                    entity,
                                ),
                            ])
                        }
                        keywords={["entity", entity.name ?? ""]}
                        shortcut={["G", (index + 1).toString()]}
                    >
                        <EntityIcon entity={entity} size="xs" /> {entity.name}
                    </SettingsCommandItem>
                );
            })}
        </Command.Group>
    );
};
